/**
 * AxLabs Simulation Core - Failure Detection
 *
 * Realistic failure modeling and detection for scientific experiments.
 * Experiments must fail correctly - this is a feature, not a bug.
 */
// ============================================================================
// NUMERIC VALIDATION
// ============================================================================
/**
 * Check if a value is NaN
 */
export function isNaN(value) {
    return Number.isNaN(value);
}
/**
 * Check if a value is infinite
 */
export function isInfinite(value) {
    return !Number.isFinite(value);
}
/**
 * Check if any value in a record is NaN
 */
export function hasNaN(values) {
    return Object.values(values).some(Number.isNaN);
}
/**
 * Check if any value in a record is infinite
 */
export function hasInfinity(values) {
    return Object.values(values).some(v => !Number.isFinite(v));
}
// ============================================================================
// ENERGY CONSERVATION
// ============================================================================
/**
 * Calculate the total energy from components
 */
export function calculateTotalEnergy(energy) {
    const total = energy.kinetic + energy.potential + energy.thermal;
    return { ...energy, total };
}
/**
 * Check energy conservation within a tolerance
 *
 * @param initialEnergy Initial total energy
 * @param currentEnergy Current total energy
 * @param tolerance Relative tolerance (default 0.01 = 1%)
 * @returns True if energy is conserved within tolerance
 */
export function checkEnergyConservation(initialEnergy, currentEnergy, tolerance = 0.01) {
    if (initialEnergy === 0) {
        return Math.abs(currentEnergy) < tolerance;
    }
    const relativeError = Math.abs((currentEnergy - initialEnergy) / initialEnergy);
    return relativeError <= tolerance;
}
/**
 * Calculate energy drift over time
 */
export function calculateEnergyDrift(energyHistory) {
    if (energyHistory.length < 2) {
        return { drift: 0, stable: true };
    }
    const initial = energyHistory[0];
    const final = energyHistory[energyHistory.length - 1];
    if (initial === undefined || final === undefined) {
        return { drift: 0, stable: true };
    }
    if (initial === 0) {
        return { drift: final, stable: Math.abs(final) < 0.01 };
    }
    const drift = (final - initial) / initial;
    return { drift, stable: Math.abs(drift) < 0.05 }; // 5% threshold
}
// ============================================================================
// NUMERICAL STABILITY
// ============================================================================
/**
 * Detect numerical instability from oscillation patterns
 *
 * Looks for growing oscillations which indicate instability.
 */
export function detectInstability(history, windowSize = 10) {
    if (history.length < windowSize * 2) {
        return false;
    }
    // Calculate variance in recent window vs older window
    const recentStart = history.length - windowSize;
    const olderStart = history.length - windowSize * 2;
    const recentWindow = history.slice(recentStart);
    const olderWindow = history.slice(olderStart, recentStart);
    const recentVariance = calculateVariance(recentWindow);
    const olderVariance = calculateVariance(olderWindow);
    // If variance is growing significantly, we have instability
    if (olderVariance === 0) {
        return recentVariance > 0.01;
    }
    return recentVariance / olderVariance > 10;
}
/**
 * Calculate variance of a number array
 */
function calculateVariance(values) {
    if (values.length === 0)
        return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const squaredDiffs = values.map(v => (v - mean) ** 2);
    return squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
}
/**
 * Detect divergence (values growing without bound)
 */
export function detectDivergence(history, threshold = 1e10) {
    if (history.length === 0)
        return false;
    const lastValue = history[history.length - 1];
    return lastValue !== undefined && Math.abs(lastValue) > threshold;
}
// ============================================================================
// BOUNDS CHECKING
// ============================================================================
/**
 * Check if a value is within bounds
 */
export function isWithinBounds(value, min, max) {
    return value >= min && value <= max;
}
/**
 * Check if all values in a record are within their bounds
 */
export function checkAllBounds(values, bounds) {
    const violations = [];
    for (const [key, value] of Object.entries(values)) {
        const bound = bounds[key];
        if (bound && !isWithinBounds(value, bound.min, bound.max)) {
            violations.push(key);
        }
    }
    return { valid: violations.length === 0, violations };
}
// ============================================================================
// NOISE INJECTION
// ============================================================================
/**
 * Seeded random number generator for reproducibility
 * Uses a simple xorshift algorithm
 */
export class SeededRandom {
    state;
    constructor(seed) {
        this.state = seed;
    }
    /**
     * Get next random number in [0, 1)
     */
    next() {
        // xorshift32
        let x = this.state;
        x ^= x << 13;
        x ^= x >>> 17;
        x ^= x << 5;
        this.state = x >>> 0;
        return (this.state >>> 0) / 0xffffffff;
    }
    /**
     * Get random number in range [min, max)
     */
    range(min, max) {
        return min + this.next() * (max - min);
    }
    /**
     * Get random number with Gaussian distribution
     * Uses Box-Muller transform
     */
    gaussian(mean = 0, stddev = 1) {
        const u1 = this.next();
        const u2 = this.next();
        // Avoid log(0)
        const safeU1 = Math.max(u1, 1e-10);
        const z0 = Math.sqrt(-2 * Math.log(safeU1)) * Math.cos(2 * Math.PI * u2);
        return mean + z0 * stddev;
    }
}
/**
 * Add Gaussian noise to a measurement
 *
 * @param value True value
 * @param sigma Standard deviation (as fraction of value)
 * @param rng Random number generator
 * @returns Noisy measurement
 */
export function addGaussianNoise(value, sigma, rng) {
    const absoluteSigma = Math.abs(value * sigma) + 1e-10; // Minimum noise floor
    return value + rng.gaussian(0, absoluteSigma);
}
/**
 * Add uniform noise to a measurement
 */
export function addUniformNoise(value, range, rng) {
    const absoluteRange = Math.abs(value * range) + 1e-10;
    return value + rng.range(-absoluteRange, absoluteRange);
}
// ============================================================================
// SIMULATION FLAGS
// ============================================================================
/**
 * Create default (healthy) simulation flags
 */
export function createHealthyFlags() {
    return {
        isStable: true,
        hasNaN: false,
        hasInfinity: false,
        energyConservationViolation: false,
        boundaryViolation: false,
        numericalInstability: false,
    };
}
/**
 * Check simulation state for failures and return updated flags
 */
export function checkSimulationHealth(state, initialEnergy, positionHistory, bounds) {
    const flags = {
        hasNaN: hasNaN(state.variables),
        hasInfinity: hasInfinity(state.variables),
        energyConservationViolation: initialEnergy !== undefined &&
            state.energy !== undefined &&
            !checkEnergyConservation(initialEnergy, state.energy.total, 0.05),
        boundaryViolation: bounds !== undefined &&
            !checkAllBounds(state.variables, bounds).valid,
        numericalInstability: detectInstability(positionHistory),
        isStable: true, // Will be updated below
    };
    // Set overall stability
    const isStable = !(flags.hasNaN ||
        flags.hasInfinity ||
        flags.energyConservationViolation ||
        flags.numericalInstability);
    return { ...flags, isStable };
}
// ============================================================================
// FAILURE MESSAGES
// ============================================================================
/**
 * Generate educational failure message from flags
 */
export function getFailureMessage(flags) {
    if (flags.isStable) {
        return undefined;
    }
    const messages = [];
    if (flags.hasNaN) {
        messages.push('Calculation produced undefined values (NaN). ' +
            'This often occurs when dividing by zero or taking the square root of negative numbers.');
    }
    if (flags.hasInfinity) {
        messages.push('Values grew beyond computational limits (Infinity). ' +
            'This indicates the system parameters may be outside physically realistic ranges.');
    }
    if (flags.energyConservationViolation) {
        messages.push('Energy is not being conserved. ' +
            'For a closed system without damping, total energy should remain constant. ' +
            'This may indicate numerical errors or unmodeled energy loss.');
    }
    if (flags.numericalInstability) {
        messages.push('Numerical instability detected - oscillations are growing over time. ' +
            'Try reducing the simulation timestep or check initial conditions.');
    }
    if (flags.boundaryViolation) {
        messages.push('System has exceeded physical boundaries. ' +
            'The experiment parameters may need adjustment.');
    }
    return messages.join(' ');
}
//# sourceMappingURL=failure.js.map